import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Zap, ArrowRight, ArrowLeft, ExternalLink, Heart, Sparkles, Check, Loader2,
  Route, Youtube, Bot,
} from "lucide-react";
import { ensureVisitorId, trackEvent, saveResponse } from "@/lib/pmf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/* ---------- animated vibrant background ---------- */
const NOISE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

function MeshBackground() {
  const blobs = [
    "bg-indigo-400/35 dark:bg-indigo-600/25 top-[-12%] left-[-10%] w-[42vw] h-[42vw]",
    "bg-pink-400/30 dark:bg-pink-600/20 bottom-[-15%] right-[-12%] w-[52vw] h-[52vw]",
    "bg-emerald-400/25 dark:bg-emerald-500/15 top-[38%] left-[58%] w-[34vw] h-[34vw]",
  ];
  const moves = [
    { x: ["0%", "12%", "-6%", "0%"], y: ["0%", "-10%", "12%", "0%"] },
    { x: ["0%", "-10%", "8%", "0%"], y: ["0%", "12%", "-8%", "0%"] },
    { x: ["0%", "8%", "-10%", "0%"], y: ["0%", "-12%", "6%", "0%"] },
  ];
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {blobs.map((b, i) => (
        <motion.div
          key={i}
          className={`absolute rounded-full blur-[100px] ${b}`}
          animate={moves[i]}
          transition={{ duration: 16 + i * 3, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
        />
      ))}
      <div
        className="absolute inset-0 opacity-[0.04] mix-blend-overlay dark:mix-blend-soft-light dark:opacity-[0.06]"
        style={{ backgroundImage: NOISE }}
      />
    </div>
  );
}

/* ---------- bouncy progress ---------- */
const STEPS = ["before", "about", "after"];
function Progress({ stage }) {
  const idx = STEPS.indexOf(stage);
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="relative h-2 flex-1 overflow-hidden rounded-full bg-slate-200/80 dark:bg-slate-800">
            {i <= idx && (
              <motion.div
                className="absolute inset-0 origin-left rounded-full bg-gradient-to-r from-indigo-500 to-violet-500"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ type: "spring", stiffness: 60, damping: 16 }}
              />
            )}
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs font-semibold text-slate-500 dark:text-slate-400">Step {idx + 1} of {STEPS.length}</p>
    </div>
  );
}

/* ---------- form primitives ---------- */
function ChoicePill({ value, current, onChange, testid, children }) {
  const active = current === value;
  return (
    <motion.button
      type="button"
      data-testid={testid}
      onClick={() => onChange(value)}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.96 }}
      className={`relative rounded-2xl border-2 px-3 py-3.5 text-sm font-bold transition-colors ${
        active
          ? "border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-200 dark:border-indigo-500"
          : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-indigo-600"
      }`}
    >
      {children}
    </motion.button>
  );
}

function PillOption({ value, current, onChange, testid, emoji, children }) {
  const active = current === value;
  return (
    <motion.button
      type="button"
      data-testid={testid}
      onClick={() => onChange(value)}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`relative flex w-full items-center justify-between gap-3 rounded-2xl border-2 p-4 text-left text-sm font-semibold transition-colors md:text-base ${
        active
          ? "border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-200 dark:border-indigo-500"
          : "border-slate-200 bg-white text-slate-700 hover:border-indigo-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-indigo-600"
      }`}
    >
      <span className="flex items-center gap-2.5">{emoji && <span className="text-lg">{emoji}</span>}{children}</span>
      <AnimatePresence>
        {active && (
          <motion.span
            initial={{ scale: 0, rotate: -30 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 18 }}
            className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-indigo-500 text-white"
          >
            <Check className="h-3.5 w-3.5" />
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

const SCALE = [
  { v: 1, emoji: "🤩", active: "border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500" },
  { v: 2, emoji: "🙂", active: "border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-400" },
  { v: 3, emoji: "😐", active: "border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-400" },
  { v: 4, emoji: "😟", active: "border-pink-500 bg-pink-50 text-pink-700 dark:bg-pink-500/15 dark:text-pink-300 dark:border-pink-500" },
  { v: 5, emoji: "😫", active: "border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500" },
];

function Scale({ value, onChange }) {
  return (
    <div className="flex items-stretch justify-between gap-2">
      {SCALE.map((s) => {
        const active = value === s.v;
        return (
          <motion.button
            key={s.v}
            type="button"
            data-testid={`q3-scale-${s.v}`}
            onClick={() => onChange(s.v)}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.94 }}
            className={`flex flex-1 flex-col items-center justify-center gap-1 rounded-2xl border-2 py-3 transition-colors ${
              active ? s.active : "border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900"
            }`}
          >
            <span className={`text-2xl transition-transform ${active ? "scale-110" : "grayscale-[35%] opacity-80"}`}>{s.emoji}</span>
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400">{s.v}</span>
          </motion.button>
        );
      })}
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div className="space-y-2.5">
      <div>
        <p className="font-heading text-base font-bold text-slate-900 dark:text-white">
          {label} {required && <span className="text-indigo-500">*</span>}
        </p>
        {hint && <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

const FEATURES = [
  [Route, "A course built around YOU", "It learns your background, goal, level and the time you have — then assembles your entire course step by step. Not a generic playlist; a path made for your needs.", "bg-indigo-500"],
  [Youtube, "Curated free YouTube lessons", "Every step is a hand-picked, genuinely good free video. No fluff, no endless searching.", "bg-emerald-500"],
  [Bot, "An AI tutor that keeps you on track", "Summaries, quizzes, practice tasks and an 'Ask AI' doubt-solver on every lesson — so it sticks.", "bg-pink-500"],
];

const inputCls =
  "rounded-xl border-2 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:border-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white";

export default function Survey() {
  const [stage, setStage] = useState("intro");
  const [submitting, setSubmitting] = useState(false);
  const [f, setF] = useState({});
  const startedRef = useRef(false);
  const visitedRef = useRef(false);

  const confetti = useMemo(
    () =>
      Array.from({ length: 20 }).map((_, i) => ({
        id: i,
        x: (Math.random() * 2 - 1) * 260,
        y: (Math.random() * 2 - 1) * 200,
        c: ["#4F46E5", "#34D399", "#DB2777", "#FB923C", "#8B5CF6"][i % 5],
        size: 7 + Math.random() * 9,
        rot: Math.random() * 360,
        delay: Math.random() * 0.15,
      })),
    []
  );

  useEffect(() => {
    ensureVisitorId();
    if (!visitedRef.current) {
      visitedRef.current = true;
      trackEvent("visited");
    }
    window.scrollTo(0, 0);
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const setQ1 = (v) => {
    set("q1", v);
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("started");
    }
  };

  const goto = (s) => { setStage(s); window.scrollTo(0, 0); };

  const partial = () => saveResponse({
    q1: f.q1, q2: f.q2, q2_other: f.q2_other, q3: f.q3, q4: f.q4, q4_text: f.q4_text,
  });

  const toAbout = () => { if (!f.q1) return; partial(); goto("about"); };

  const submitFinal = async () => {
    if (!f.q6) return;
    setSubmitting(true);
    const num = (x) => (x === "" || x == null || isNaN(Number(x)) ? null : Number(x));
    await saveResponse({
      q1: f.q1, q2: f.q2, q2_other: f.q2_other, q3: f.q3, q4: f.q4, q4_text: f.q4_text,
      q5: f.q5, q6: f.q6,
      price_too_expensive: num(f.price_too_expensive),
      price_too_cheap: num(f.price_too_cheap),
      price_fair: num(f.price_fair),
      email: (f.email || "").trim() || null,
      completed: true,
    });
    trackEvent("completed");
    if ((f.email || "").trim()) trackEvent("left_email");
    setSubmitting(false);
    goto("done");
  };

  const openDemo = () => {
    trackEvent("clicked_try");
    const vid = ensureVisitorId();
    window.open(`/?pmf=${encodeURIComponent(vid)}`, "_blank", "noopener");
  };

  const stageMotion = {
    initial: { opacity: 0, x: 24, filter: "blur(6px)" },
    animate: { opacity: 1, x: 0, filter: "blur(0px)" },
    exit: { opacity: 0, x: -24, filter: "blur(6px)" },
    transition: {
      type: "spring",
      stiffness: 240,
      damping: 26,
      filter: { duration: 0.3, ease: "easeOut" },
      opacity: { duration: 0.3, ease: "easeOut" },
    },
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-white">
      <MeshBackground />

      <header className="relative z-10">
        <div className="mx-auto flex h-16 max-w-xl items-center gap-2.5 px-5">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-indigo-600 text-white shadow-lg shadow-indigo-500/30">
            <Zap className="h-5 w-5 fill-current" />
          </span>
          <span className="font-heading text-lg font-extrabold tracking-tight">AI Pathshaalaa</span>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-xl px-5 py-6 sm:py-10" data-testid="survey-page">
        {stage !== "intro" && stage !== "done" && <Progress stage={stage} />}

        <div className="rounded-3xl border border-white/60 bg-white/70 p-6 shadow-2xl shadow-slate-900/5 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/70 sm:p-8">
          <AnimatePresence mode="wait">
            <motion.div key={stage} {...stageMotion}>
              {/* INTRO */}
              {stage === "intro" && (
                <div data-testid="survey-intro" className="space-y-5">
                  <span className="inline-flex items-center gap-2 rounded-full bg-indigo-100 px-4 py-2 text-sm font-bold text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
                    <motion.span animate={{ rotate: [0, -12, 12, -12, 0] }} transition={{ repeat: Infinity, repeatDelay: 2, duration: 0.6 }}>
                      <Heart className="h-4 w-4 fill-current" />
                    </motion.span>
                    2-minute honest check
                  </span>
                  <h1 className="font-heading text-4xl font-black leading-tight tracking-tighter sm:text-5xl">
                    I built something — and I need your{" "}
                    <span className="bg-gradient-to-r from-indigo-600 via-violet-500 to-pink-500 bg-clip-text text-transparent">honest feedback</span>.
                  </h1>
                  <p className="leading-relaxed text-slate-600 dark:text-slate-300">
                    Please be <b className="text-slate-900 dark:text-white">truthful, not nice</b>. Brutal honesty helps me far more than encouragement. This takes under 2 minutes.
                  </p>
                  <div className="rounded-2xl border-2 border-slate-100 bg-white p-5 dark:border-slate-700 dark:bg-slate-800/60">
                    <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">
                      <b className="text-slate-900 dark:text-white">AI Pathshaalaa</b> turns the scattered mess of AI videos on YouTube into one clear, personalized path — so you actually learn AI instead of drowning in random tutorials.
                    </p>
                  </div>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button data-testid="survey-start" onClick={() => goto("before")}
                      className="w-full rounded-2xl bg-indigo-600 py-6 text-base font-bold text-white shadow-[0_10px_30px_-8px_rgba(79,70,229,0.7)] hover:bg-indigo-500">
                      Let's go <ArrowRight className="h-4 w-4" />
                    </Button>
                  </motion.div>
                </div>
              )}

              {/* SECTION A — your situation */}
              {stage === "before" && (
                <div data-testid="survey-before" className="space-y-8">
                  <p className="text-sm font-bold text-slate-500 dark:text-slate-400">First, a bit about you 👇</p>

                  <Field label="Do you want to learn AI right now?" required>
                    <div className="grid grid-cols-3 gap-2">
                      <ChoicePill value="yes" current={f.q1} onChange={setQ1} testid="q1-yes">Yes</ChoicePill>
                      <ChoicePill value="no" current={f.q1} onChange={setQ1} testid="q1-no">No</ChoicePill>
                      <ChoicePill value="not_sure" current={f.q1} onChange={setQ1} testid="q1-not_sure">Not sure</ChoicePill>
                    </div>
                  </Field>

                  <Field label="How are you currently trying to learn AI?">
                    <div className="grid gap-2">
                      <PillOption value="youtube" current={f.q2} onChange={(v) => set("q2", v)} testid="q2-youtube" emoji="▶️">YouTube videos</PillOption>
                      <PillOption value="course" current={f.q2} onChange={(v) => set("q2", v)} testid="q2-course" emoji="🎓">A paid course or app</PillOption>
                      <PillOption value="not_trying" current={f.q2} onChange={(v) => set("q2", v)} testid="q2-not_trying" emoji="😴">Not really trying yet</PillOption>
                      <PillOption value="other" current={f.q2} onChange={(v) => set("q2", v)} testid="q2-other" emoji="✨">Other</PillOption>
                    </div>
                    {f.q2 === "other" && (
                      <Input data-testid="q2-other-text" value={f.q2_other || ""} onChange={(e) => set("q2_other", e.target.value)}
                        placeholder="Tell me how (optional)" className={`mt-2 ${inputCls}`} />
                    )}
                  </Field>

                  <Field label='How frustrating is the "too many scattered videos, no clear path" problem?' hint="1 = not at all · 5 = extremely frustrating">
                    <Scale value={f.q3} onChange={(v) => set("q3", v)} />
                  </Field>

                  <Field label="Have you ever actually paid for an online course or learning app before?">
                    <div className="grid grid-cols-2 gap-2">
                      <ChoicePill value="yes" current={f.q4} onChange={(v) => set("q4", v)} testid="q4-yes">Yes</ChoicePill>
                      <ChoicePill value="no" current={f.q4} onChange={(v) => set("q4", v)} testid="q4-no">No</ChoicePill>
                    </div>
                    {f.q4 === "yes" && (
                      <Input data-testid="q4-text" value={f.q4_text || ""} onChange={(e) => set("q4_text", e.target.value)}
                        placeholder="What did you pay for? (optional)" className={`mt-2 ${inputCls}`} />
                    )}
                  </Field>

                  <motion.div whileHover={{ scale: f.q1 ? 1.02 : 1 }} whileTap={{ scale: f.q1 ? 0.98 : 1 }}>
                    <Button data-testid="survey-to-about" onClick={toAbout} disabled={!f.q1}
                      className="w-full rounded-2xl bg-indigo-600 py-6 text-base font-bold text-white shadow-[0_10px_30px_-8px_rgba(79,70,229,0.7)] hover:bg-indigo-500 disabled:opacity-50 disabled:shadow-none">
                      Next <ArrowRight className="h-4 w-4" />
                    </Button>
                  </motion.div>
                </div>
              )}

              {/* ABOUT — here's what it does */}
              {stage === "about" && (
                <div data-testid="survey-about" className="space-y-6">
                  <div className="space-y-2 text-center">
                    <motion.span
                      initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 240, damping: 14 }}
                      className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-lg shadow-indigo-500/30">
                      <Sparkles className="h-7 w-7" />
                    </motion.span>
                    <h2 className="font-heading text-3xl font-black tracking-tighter sm:text-4xl">Here's what AI Pathshaalaa does</h2>
                    <p className="text-slate-600 dark:text-slate-300">A quick look, so your answers next are informed. You'll get to try the real thing at the end.</p>
                  </div>

                  <div className="rounded-2xl border-2 border-indigo-100 bg-indigo-50/70 p-4 text-center dark:border-indigo-500/20 dark:bg-indigo-500/10">
                    <p className="text-sm font-semibold leading-relaxed text-slate-700 dark:text-slate-200">
                      🎯 We curate your <b className="text-indigo-600 dark:text-indigo-300">entire course around your background, goals and needs</b> — not a one-size-fits-all playlist.
                    </p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    {FEATURES.map(([Icon, title, desc, bg], i) => (
                      <motion.div
                        key={title}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + i * 0.08 }}
                        whileHover={{ y: -8, rotate: 1 }}
                        className="relative overflow-hidden rounded-3xl border border-slate-100 bg-white p-5 shadow-lg dark:border-slate-700 dark:bg-slate-800"
                      >
                        <span className={`mb-3 grid h-11 w-11 place-items-center rounded-2xl text-white shadow-inner ${bg}`}>
                          <Icon className="h-5 w-5" />
                        </span>
                        <p className="font-heading text-base font-bold text-slate-900 dark:text-white">{title}</p>
                        <p className="mt-1 text-sm leading-relaxed text-slate-500 dark:text-slate-400">{desc}</p>
                      </motion.div>
                    ))}
                  </div>

                  <p className="text-center text-sm text-slate-500 dark:text-slate-400">
                    We charge for the curation + tools — <b className="text-slate-900 dark:text-white">the videos stay free</b>.
                  </p>

                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button data-testid="survey-to-after" onClick={() => goto("after")}
                      className="w-full rounded-2xl bg-indigo-600 py-6 text-base font-bold text-white shadow-[0_10px_30px_-8px_rgba(79,70,229,0.7)] hover:bg-indigo-500">
                      Got it — give my honest take <ArrowRight className="h-4 w-4" />
                    </Button>
                  </motion.div>
                  <button onClick={() => goto("before")} className="mx-auto flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back
                  </button>
                </div>
              )}

              {/* SECTION C — your take */}
              {stage === "after" && (
                <div data-testid="survey-after" className="space-y-8">
                  <p className="text-sm font-bold text-slate-500 dark:text-slate-400">Now the honest part 🙏</p>

                  <Field label="What's your honest first reaction to this idea?" hint="Pick the one that fits best — be harsh, I can take it.">
                    <div className="grid gap-2">
                      <PillOption value="love_it" current={f.q5} onChange={(v) => set("q5", v)} testid="q5-love_it" emoji="😍">Love it — I'd genuinely use this</PillOption>
                      <PillOption value="useful" current={f.q5} onChange={(v) => set("q5", v)} testid="q5-useful" emoji="🙂">Useful — I can see the value</PillOption>
                      <PillOption value="meh" current={f.q5} onChange={(v) => set("q5", v)} testid="q5-meh" emoji="😐">Meh — not sure it's for me</PillOption>
                      <PillOption value="confusing" current={f.q5} onChange={(v) => set("q5", v)} testid="q5-confusing" emoji="😕">Confusing — I don't fully get it</PillOption>
                      <PillOption value="not_useful" current={f.q5} onChange={(v) => set("q5", v)} testid="q5-not_useful" emoji="🙁">Not useful / pointless to me</PillOption>
                    </div>
                  </Field>

                  <Field label="Would you actually use this?" required>
                    <div className="grid grid-cols-3 gap-2">
                      <ChoicePill value="yes" current={f.q6} onChange={(v) => set("q6", v)} testid="q6-yes">😍 Yes</ChoicePill>
                      <ChoicePill value="maybe" current={f.q6} onChange={(v) => set("q6", v)} testid="q6-maybe">🤔 Maybe</ChoicePill>
                      <ChoicePill value="no" current={f.q6} onChange={(v) => set("q6", v)} testid="q6-no">🙅 No</ChoicePill>
                    </div>
                  </Field>

                  <Field label="Pricing — for a full YEAR of access" hint="Just your gut feel, in ₹. Leave blank if unsure.">
                    <div className="space-y-3">
                      <div>
                        <p className="mb-1 text-sm text-slate-500 dark:text-slate-400">At what price would it feel <b className="text-slate-900 dark:text-white">too expensive</b> to bother?</p>
                        <Input data-testid="price-expensive" type="number" inputMode="numeric" value={f.price_too_expensive || ""}
                          onChange={(e) => set("price_too_expensive", e.target.value)} placeholder="₹ e.g. 3000" className={inputCls} />
                      </div>
                      <div>
                        <p className="mb-1 text-sm text-slate-500 dark:text-slate-400">At what price would it feel <b className="text-slate-900 dark:text-white">so cheap</b> you'd doubt the quality?</p>
                        <Input data-testid="price-cheap" type="number" inputMode="numeric" value={f.price_too_cheap || ""}
                          onChange={(e) => set("price_too_cheap", e.target.value)} placeholder="₹ e.g. 199" className={inputCls} />
                      </div>
                      <div>
                        <p className="mb-1 text-sm text-slate-500 dark:text-slate-400">What yearly price would feel <b className="text-slate-900 dark:text-white">genuinely fair</b>?</p>
                        <Input data-testid="price-fair" type="number" inputMode="numeric" value={f.price_fair || ""}
                          onChange={(e) => set("price_fair", e.target.value)} placeholder="₹ e.g. 999" className={inputCls} />
                      </div>
                    </div>
                  </Field>

                  <Field label="Want early access & updates?" hint="Optional — drop your email and I'll keep you posted.">
                    <Input data-testid="q8-email" type="email" value={f.email || ""} onChange={(e) => set("email", e.target.value)}
                      placeholder="you@email.com" className={inputCls} />
                  </Field>

                  <motion.div whileHover={{ scale: f.q6 ? 1.02 : 1 }} whileTap={{ scale: f.q6 ? 0.98 : 1 }}>
                    <Button data-testid="survey-submit" onClick={submitFinal} disabled={!f.q6 || submitting}
                      className="w-full rounded-2xl bg-indigo-600 py-6 text-base font-bold text-white shadow-[0_10px_30px_-8px_rgba(79,70,229,0.7)] hover:bg-indigo-500 disabled:opacity-50 disabled:shadow-none">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit feedback <ArrowRight className="h-4 w-4" /></>}
                    </Button>
                  </motion.div>
                </div>
              )}

              {/* DONE — thank you + try it last */}
              {stage === "done" && (
                <div data-testid="survey-done" className="relative space-y-5 py-6 text-center">
                  {/* confetti burst */}
                  <div className="pointer-events-none absolute left-1/2 top-16 h-0 w-0">
                    {confetti.map((c) => (
                      <motion.span
                        key={c.id}
                        className="absolute rounded-[2px]"
                        style={{ width: c.size, height: c.size, backgroundColor: c.c }}
                        initial={{ x: 0, y: 0, scale: 0, opacity: 1, rotate: 0 }}
                        animate={{ x: c.x, y: c.y, scale: 1, opacity: 0, rotate: c.rot }}
                        transition={{ duration: 1.3, delay: c.delay, ease: "easeOut" }}
                      />
                    ))}
                  </div>

                  <motion.span
                    initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 12 }}
                    className="relative mx-auto grid h-20 w-20 place-items-center rounded-full bg-emerald-500 text-white shadow-xl shadow-emerald-500/40">
                    <svg viewBox="0 0 52 52" className="h-10 w-10">
                      <motion.path d="M14 27 l8 8 l16 -18" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"
                        initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.5, delay: 0.35 }} />
                    </svg>
                  </motion.span>

                  <h2 className="font-heading text-4xl font-black tracking-tighter sm:text-5xl">Thank you — truly. 🙏</h2>
                  <p className="mx-auto max-w-sm leading-relaxed text-slate-600 dark:text-slate-300">
                    Your honesty genuinely helps me build something people actually want. I read every single response.
                  </p>

                  <div className="mt-2 rounded-3xl border-2 border-indigo-100 bg-gradient-to-br from-indigo-50 to-violet-50 p-5 dark:border-indigo-500/20 dark:from-indigo-500/10 dark:to-violet-500/10">
                    <p className="font-heading text-lg font-bold text-slate-900 dark:text-white">Now, want to see it in action?</p>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Try the real thing — build your own AI path in a couple of minutes.</p>
                    <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}>
                      <Button data-testid="survey-open-app" onClick={openDemo}
                        className="mt-4 w-full rounded-2xl bg-indigo-600 py-6 text-base font-bold text-white shadow-[0_0_40px_-10px_rgba(79,70,229,0.8)] hover:bg-indigo-500">
                        Try AI Pathshaalaa <ExternalLink className="h-4 w-4" />
                      </Button>
                    </motion.div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
